import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface SuccessCardProps {
  podcastName: string;
  episodeCount: number | null;
  successMessage: string;
  heading?: string;
  episodeDetail?: string;
  amountChargedCents?: number;
  creditsUsedCents?: number;
  onAddAnother?: () => void;
  className?: string;
}

export default function SuccessCard({
  podcastName,
  episodeCount,
  successMessage,
  heading = "Subscription queued!",
  episodeDetail,
  amountChargedCents,
  creditsUsedCents,
  onAddAnother,
  className,
}: SuccessCardProps) {
  const navigate = useNavigate();

  return (
    <div
      className={cn(
        "rounded-2xl px-5 py-4 space-y-4",
        theme.borders.neubrutalism,
        theme.colors.primaryOrange,
        "text-gray-900 dark:text-white",
        theme.shadows.neubrutalismSm,
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 flex-shrink-0">
          <CheckCircle2 className="w-full h-full text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h3 className="text-xl font-semibold">{heading}</h3>
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-base font-medium">{podcastName}</p>
        {episodeDetail ? (
          <p className="text-sm text-gray-700 dark:text-gray-200">{episodeDetail}</p>
        ) : episodeCount !== null && (
          <p className="text-sm text-gray-700 dark:text-gray-200">{episodeCount} episodes queued</p>
        )}
      </div>

      <p className="text-sm sm:text-base leading-relaxed">{successMessage}</p>

      {(amountChargedCents !== undefined || creditsUsedCents !== undefined) && (
        <div className="text-sm font-medium">
          {amountChargedCents !== undefined && amountChargedCents > 0 && (
            <p>Charged: ${(amountChargedCents / 100).toFixed(2)}</p>
          )}
          {creditsUsedCents !== undefined && creditsUsedCents > 0 && (
            <p>Used ${(creditsUsedCents / 100).toFixed(2)} in credits</p>
          )}
          {amountChargedCents === 0 && creditsUsedCents === 0 && (
            <p>No charge (covered by credits)</p>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate("/episodes")}
          className={cn(
            "flex-1 inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-semibold min-h-[3.25rem]",
            theme.buttons.secondary,
          )}
        >
          View Episodes
        </button>
        {onAddAnother && (
          <button
            type="button"
            onClick={onAddAnother}
            className={cn(
              "flex-1 inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-semibold min-h-[3.25rem]",
              theme.buttons.secondary,
            )}
          >
            Add Another Podcast
          </button>
        )}
      </div>
    </div>
  );
}


